import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Building2,
  DollarSign,
  Package,
  TrendingUp,
} from "lucide-react";
import {
  getCompanyName,
} from "../lib/textron-data-processor";
import React, { useState, useEffect } from "react";
import { Loader2, FileText, Calculator } from "lucide-react";
import { Button } from "./ui/button";

// Helper function to format value in crores
const formatCrores = (value: number): string => {
  const crores = value / 10000000;
  return `₹${Math.abs(crores).toFixed(2)} Cr`;
};

// Helper function to parse date string (DD/MM/YY format)
const parseDate = (dateStr: string): Date => {
  const [day, month, year] = dateStr.split('/');
  // Convert 2-digit year to 4-digit (assuming 20xx)
  const fullYear = parseInt(year) < 50 ? 2000 + parseInt(year) : 1900 + parseInt(year);
  return new Date(fullYear, parseInt(month) - 1, parseInt(day));
};

// Helper function to find latest date from JSON keys
const findLatestDate = (data: any): string | null => {
  const dates = Object.keys(data).filter(key => {
    // Check if key matches date format DD/MM/YY
    return /^\d{2}\/\d{2}\/\d{2}$/.test(key);
  });
  
  if (dates.length === 0) return null;
  
  // Sort dates and return the latest
  dates.sort((a, b) => {
    const dateA = parseDate(a);
    const dateB = parseDate(b);
    return dateB.getTime() - dateA.getTime();
  });
  
  return dates[0];
};

interface FinancialData {
  formulae?: {
    Current_ratio?: number;
    Inventory_turnover_ratio?: number;
    Inventory_days?: number;
    Debtor_turnover_ratio?: number;
    Debtor_days?: number;
    Creditor_turnover_ratio?: number;
    Creditor_days?: number;
    Operating_cycle?: number;
    Net_profit_ratio?: number;
    Debt_equity_ratio?: number;
  };
  pl_as?: {
    "Revenue from Operations"?: number;
    "Other Income"?: number;
    "TOTAL REVENUE"?: number;
    "TOTAL EXPENSES"?: number;
    "PROFIT/(LOSS) FOR THE PERIOD"?: number;
    [key: string]: number | undefined;
  };
  pl_as_ye?: {
    [key: string]: number | undefined;
  };
  pl_as_qe?: {
    [key: string]: number | undefined;
  };
  bs_as?: {
    "Total Assets"?: number;
    "Current Assets"?: number;
    "Current Liabilities"?: number;
    "Non-Current Assets"?: number;
    "Long-term borrowings"?: number;
    "Short-term borrowings"?: number;
    "Total outstanding dues of MSME"?: number;
    "Total outstanding dues of creditors other than MSME"?: number;
    "Other current liabilities"?: number;
    [key: string]: number | undefined;
  };
  other_exceptions?: {
    [key: string]: number | undefined;
  };
}

export function OverviewSection() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [companyName, setCompanyName] = useState<string>("");
  const [financialData, setFinancialData] = useState<FinancialData | null>(null);
  const [latestDate, setLatestDate] = useState<string | null>(null);

  // Load financial data from SharePoint
  const loadFinancialData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading Financial Data');

      // Get company name
      const companyNameResult = await getCompanyName("combined");
      setCompanyName(companyNameResult);

      // Download FinData_Formulae.json from SharePoint
      if ((window as any).sharePointAPI?.downloadClientJson) {
        const result = await (window as any).sharePointAPI.downloadClientJson("FinData_Formulae.json");
        
        if (result?.success && result.data) {
          const data = result.data;
          
          // Find latest date
          const latest = findLatestDate(data);
          if (!latest) {
            throw new Error("No valid date found in financial data");
          }
          
          setLatestDate(latest);
          setFinancialData(data[latest] as FinancialData);
          setDataLoaded(true);
          console.log('Financial data loaded successfully for date:', latest);
        } else {
          throw new Error(result?.error || "Failed to download financial data");
        }
      } else {
        throw new Error("SharePoint API not available");
      }
    } catch (err) {
      console.error('Failed to load Financial data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Auto-load data when component mounts
  useEffect(() => {
    loadFinancialData();
  }, []);

  // Calculate values from financial data
  const totalAssets = financialData?.bs_as?.["Total Assets"] || 0;
  const totalRevenue =
    financialData?.pl_as_ye?.["TOTAL REVENUE"] ??
    financialData?.pl_as?.["TOTAL REVENUE"] ??
    0;
  const profitLoss =
    financialData?.pl_as_ye?.["PROFIT/(LOSS) FOR THE PERIOD"] ??
    financialData?.pl_as?.["PROFIT/(LOSS) FOR THE PERIOD"] ??
    0;
  const currentAssets = financialData?.bs_as?.["Current Assets"] || 0;
  const currentLiabilities = financialData?.bs_as?.["Current Liabilities"] || 0;
  const workingCapital = currentAssets - currentLiabilities;
  
  const currentRatio = financialData?.formulae?.Current_ratio || 0;
  const debtEquityRatio = financialData?.formulae?.Debt_equity_ratio || 0;
  const netProfitRatio = financialData?.formulae?.Net_profit_ratio || 0;

  // Get top 4 assets from bs_as
  const getTopAssets = () => {
    if (!financialData?.bs_as) return [];
    
    const assets = Object.entries(financialData.bs_as)
      .filter(([key]) => {
        // Exclude calculated fields and liabilities
        const excludeKeys = [
          "Total Assets", "Total Liabilities", "Current Assets", "Current Liabilities",
          "Non-Current Assets", "Non-Current Liabilities", "Shareholder Fund",
          "Long-term borrowings", "Short-term borrowings", "Total outstanding dues of MSME",
          "Total outstanding dues of creditors other than MSME", "Other current liabilities",
          "Long-term Provisions", "Short-term Provisions"
        ];
        return !excludeKeys.includes(key);
      })
      .map(([name, value]) => ({
        name,
        value: value || 0
      }))
      .filter(item => item.value > 0)
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
      .slice(0, 4);
    
    return assets;
  };

  const topAssets = getTopAssets();

  const overviewMetrics = [
    {
      title: "Total Assets",
      value: formatCrores(totalAssets),
      valueColor: totalAssets >= 0 ? "text-emerald-400" : "text-red-400",
      change: "",
      trend: "up",
      icon: Building2,
      description: "Total assets from balance sheet",
    },
    {
      title: "Total Revenue",
      value: formatCrores(totalRevenue),
      valueColor: totalRevenue >= 0 ? "text-emerald-400" : "text-red-400",
      change: "",
      trend: "up",
      icon: TrendingUp,
      description: "TOTAL REVENUE",
    },
    {
      title: "PROFIT/(LOSS) FOR THE PERIOD",
      value: formatCrores(profitLoss),
      valueColor: profitLoss >= 0 ? "text-emerald-400" : "text-red-400",
      change: "",
      trend: profitLoss >= 0 ? "up" : "down",
      icon: DollarSign,
      description: "Profit or loss for the period",
    },
    {
      title: "Working Capital",
      value: formatCrores(workingCapital),
      valueColor: workingCapital >= 0 ? "text-emerald-400" : "text-red-400",
      change: "",
      trend: workingCapital >= 0 ? "up" : "down",
      icon: Package,
      description: "Current Assets - Current Liabilities",
    },
  ];

  // Current ratio status
  const currentRatioStatus = currentRatio >= 2 ? "Good" : currentRatio >= 1 ? "Moderate" : "LOW";
  const currentRatioVariant = currentRatio >= 2 ? "default" : currentRatio >= 1 ? "secondary" : "destructive";
  
  // Debt-Equity ratio status
  const debtEquityStatus = debtEquityRatio > 2 ? "HIGH" : debtEquityRatio >= 1 ? "Moderate" : "LOW";
  const debtEquityVariant = debtEquityRatio > 2 ? "destructive" : debtEquityRatio >= 1 ? "secondary" : "default";

  // Show load button if data hasn't been loaded yet
  if (!dataLoaded && !loading) {
    return (
      <div className="flex h-full items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <h2 className="text-3xl font-bold text-white mb-6">Financial Overview</h2>
          <p className="text-white/80 text-lg">Close Trial Balance for these pages to load</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-white" />
          <span className="text-white">Loading Financial data</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-400 mb-2">Error loading data</div>
          <div className="text-sm text-white/60 mb-4">{error}</div>
          <Button 
            onClick={loadFinancialData}
            className="mt-4 bg-[#4da3ff] hover:bg-[#3a8bcc] text-white"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-white">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">
          Financial Overview
        </h2>
        <p className="text-white/60">
          Key financial metrics and performance indicators for {companyName}
        </p>
      </div>

      {latestDate && (
        <div className="text-sm text-white/60 mb-4">
          Data as of: <span className="font-semibold text-white">{latestDate}</span>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {overviewMetrics.map((metric, index) => (
          <Card key={index} className="relative overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {metric.title}
              </CardTitle>
              <metric.icon className="h-4 w-4 text-white/50" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${metric.valueColor}`}>{metric.value}</div>
              <div className="flex items-center space-x-2 text-xs text-white/60 mt-2">
                <span>{metric.description}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Financial Health Score</CardTitle>
            <CardDescription>
              Overall assessment based on key ratios
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Current Ratio</span>
                <Badge variant={currentRatioVariant}>
                  {currentRatioStatus}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Debt-Equity Ratio</span>
                <Badge variant={debtEquityVariant}>
                  {debtEquityStatus}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Profit Margin</span>
                <span className="text-sm font-semibold">{(netProfitRatio * 100).toFixed(2)}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Key Financial Highlights</CardTitle>
            <CardDescription>
              Important financial metrics
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-sm">Non-Current Assets</span>
                <span className={`font-medium ${(financialData?.bs_as?.["Non-Current Assets"] || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {formatCrores(Math.abs(financialData?.bs_as?.["Non-Current Assets"] || 0))}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Borrowings</span>
                <span className={`font-medium ${((financialData?.bs_as?.["Long-term borrowings"] || 0) + (financialData?.bs_as?.["Short-term borrowings"] || 0)) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {formatCrores(Math.abs((financialData?.bs_as?.["Long-term borrowings"] || 0) + (financialData?.bs_as?.["Short-term borrowings"] || 0)))}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Payables</span>
                <span className={`font-medium ${((financialData?.bs_as?.["Total outstanding dues of MSME"] || 0) + (financialData?.bs_as?.["Total outstanding dues of creditors other than MSME"] || 0) + (financialData?.bs_as?.["Other current liabilities"] || 0)) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {formatCrores(Math.abs((financialData?.bs_as?.["Total outstanding dues of MSME"] || 0) + (financialData?.bs_as?.["Total outstanding dues of creditors other than MSME"] || 0) + (financialData?.bs_as?.["Other current liabilities"] || 0)))}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">TOTAL EXPENSES</span>
                <span
                  className={`font-medium ${
                    (financialData?.pl_as_ye?.["TOTAL EXPENSES"] ??
                      financialData?.pl_as?.["TOTAL EXPENSES"] ??
                      0) >= 0
                      ? "text-emerald-400"
                      : "text-red-400"
                  }`}
                >
                  {formatCrores(
                    Math.abs(
                      financialData?.pl_as_ye?.["TOTAL EXPENSES"] ??
                        financialData?.pl_as?.["TOTAL EXPENSES"] ??
                        0
                    )
                  )}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Top Assets</CardTitle>
            <CardDescription>4 highest values from balance sheet</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {topAssets.length > 0 ? (
                topAssets.map((asset, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {asset.name}
                      </p>
                    </div>
                    <span className={`ml-2 text-sm font-semibold ${asset.value >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {formatCrores(Math.abs(asset.value))}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-white/60">No asset data available</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Revenue Composition</CardTitle>
            <CardDescription>Revenue from operations and other income</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    Revenue from Operations
                  </p>
                </div>
                <span className={`ml-2 text-sm font-semibold ${(financialData?.pl_as?.["Revenue from Operations"] || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {formatCrores(Math.abs(financialData?.pl_as?.["Revenue from Operations"] || 0))}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    Other Income
                  </p>
                </div>
                <span className={`ml-2 text-sm font-semibold ${(financialData?.pl_as?.["Other Income"] || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {formatCrores(Math.abs(financialData?.pl_as?.["Other Income"] || 0))}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
